import { Box } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MultiplayerCursor from "../../common/components/MultiplayerCursor";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import { ProductModal } from "../../components/ProductModal";
import { ProductImage } from "../../components/StudioToolbar/ProductImage";
import StudioToolbar, {
  STUDIO_TOOLBAR_HEIGHT,
} from "../../components/StudioToolbar/StudioToolbar";
import { useAppLoading } from "../../context/AppLoading/useAppLoading";
import { StudioContextProvider } from "../../context/Studio/StudioContextProvider";
import { ROUTES } from "../../constant/common.constant";
import { useDesignState } from "../../utils/ReactHooks";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import StudioLeftSidebar from "../StudioSidebarContainer/StudioLeftSidebar/StudioLeftSidebar";
import StudioRightSidebar from "../StudioSidebarContainer/StudioRightSidebar/StudioRightSidebar";
import { ProjectDesignSelector } from "./ProjectDesignSelector";
import MobileBottomBar from "./components/MobileBottomBar";
import { useSupportWidget } from "../../common/hooks/useSupportWidget";
import { useCursorPointerWebsocket } from "../../context/Multiplayer/hooks/useCursorPointerWebsocket";
import { useAuth } from "../../common/hooks/useAuth";
import { useProject } from "../../context/Project/useProject";
import { useTeam } from "../../context/Team";
import { useDesign } from "../../context/Design/useDesign";

export const StudioContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { designId } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useThemeBreakpoints();
  const { isAppLoading, setIsAppLoading } = useAppLoading();
  const { shiftWidget, unshiftWidget } = useSupportWidget();
  const designState = useDesignState(designId);
  const { teamId, isTeamsLoading } = useTeam();
  const { project, projectId, isProjectLoading } = useProject();
  const { createDesign, design, isDesignLoading } = useDesign();
  const { isProductDefLoading, hasLoadingFailed, loadingFailedErrorKey } =
    designState;

  const { userInfo } = useAuth();

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [currentVisibleSide, setCurrentVisibleSide] = useState<string>("front");

  const { cursorsOfUsersConnected, previewContainerRef } =
    useCursorPointerWebsocket({
      userId: userInfo.id,
      isMobile,
      currentVisibleSide,
    });

  usePageTitle("pages.design.title", {
    id: design?.name,
  });

  const productName = design?.product?.name;

  //If loading has failed from designState, we go to home page and let user know there was an issue
  useEffect(() => {
    if (hasLoadingFailed) {
      toast.error(t(`errors.toast.${loadingFailedErrorKey}`), {
        toastId: loadingFailedErrorKey,
      });
      setIsAppLoading(false);
      navigate(ROUTES.HOME, { replace: true });
      return;
    }
  }, [hasLoadingFailed, loadingFailedErrorKey, navigate, setIsAppLoading, t]);

  useEffect(() => {
    if (containerRef) {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, [containerRef]);

  useEffect(() => {
    if (isMobile) {
      shiftWidget();
    } else {
      unshiftWidget();
    }

    return () => {
      if (isMobile) {
        unshiftWidget();
      }
    };
  }, [isMobile, shiftWidget, unshiftWidget]);

  const isLoading =
    isProductDefLoading ||
    !project ||
    isTeamsLoading ||
    isProjectLoading ||
    isDesignLoading;

  useEffect(() => {
    setIsAppLoading(isProductDefLoading || !project);
  }, [isProductDefLoading, project, setIsAppLoading]);

  return (
    <StudioContextProvider
      value={{
        isLoading,
        designState,
        designId: designId ?? "",
        productName: productName ?? "",
        projectId: projectId ?? "",
        teamId: teamId,
        projectName: project?.name,
        isDesignLocked: !!design?.locked,
        editorConfig: {
          toolbar: {
            breadcrumbs: {
              backPageRoute: generatePath(ROUTES.PROJECT, {
                projectId: projectId ?? "",
                teamId: teamId ?? "",
              }),
              backPageLabel: project?.name ?? "",
              backPageTooltip: t("studio_toolbar.back_to_project"),
            },
          },
          redirectToEditorOnDuplicate: true,
        },
      }}
    >
      <Box ref={containerRef} maxHeight={"100dvh"} data-test="studioContainer">
        <Box>
          <StudioToolbar designName={design?.name ?? ""} />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: { xs: "space-between", lg: undefined },
          }}
          minHeight={{
            xs: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
            lg: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
          }}
          maxHeight={{
            xs: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
            lg: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
          }}
        >
          <Box>
            <ProjectDesignSelector
              onAddButtonClick={() => setIsProductModalOpen(true)}
            />
          </Box>
          <Box
            sx={{ display: "flex", overflow: "hidden" }}
            width={"100%"}
            flex={1}
          >
            <React.Fragment>
              {cursorsOfUsersConnected.map((c) => (
                <MultiplayerCursor
                  previewContainerRef={previewContainerRef}
                  xCoordinates={c.x}
                  yCoordinates={c.y}
                  color={c.color}
                  side={c.side}
                  key={c.userId}
                />
              ))}
              {!isMobile && <StudioLeftSidebar />}
              <ProductImage
                previewContainerRef={previewContainerRef}
                onVisibleSideChange={setCurrentVisibleSide}
              />
              {!isMobile && <StudioRightSidebar />}
            </React.Fragment>
          </Box>
          {isMobile && <MobileBottomBar />}
        </Box>

        <ProductModal
          onCloseModal={() => setIsProductModalOpen(false)}
          createDesign={createDesign}
          open={isProductModalOpen && !isAppLoading}
        />
      </Box>
    </StudioContextProvider>
  );
};
